import { Component, DestroyRef, effect, Inject, inject, OnInit, signal } from '@angular/core';
import { debounceTime, map, skip, switchMap } from 'rxjs/operators';
import {
  Conversation,
  MeetingAnalysisApiService,
  MeetingBot,
  MeetingBotApiService,
  PrivacyLevel,
} from '@vendasta/meeting-analysis';
import {
  MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$,
  MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$,
  MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$,
  MEETING_ANALYSIS_ENABLED_INJECTION_TOKEN$,
  MeetingAnalysisRouterService,
  Context,
  MEETING_ANALYSIS_CONTEXT_INJECTION_TOKEN$,
} from '@galaxy/meeting-analysis/static';
import { BehaviorSubject, catchError, combineLatest, Observable, of, take } from 'rxjs';
import { CommonModule, DatePipe, KeyValue, KeyValuePipe } from '@angular/common';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { MatExpansionModule } from '@angular/material/expansion';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MeetingBotListItemComponent } from './meeting-bot-list-item/meeting-bot-list-item.component';
import { MeetingListItemComponent } from './meeting-list-item/meeting-list-item.component';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { GalaxyDataSource, GalaxyTableModule } from '@vendasta/galaxy/table';
import { MatTableModule } from '@angular/material/table';
import {
  ConversationColumns,
  ConversationDataSourceService,
  ConversationRow,
} from './conversation-data-source.service';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import {
  GalaxyFilterChipInjectionToken,
  GalaxyFilterChipsModule,
  GalaxyFilterInterface,
} from '@vendasta/galaxy/filter/chips';
import { ConversationPagePathPipe } from '@galaxy/crm/static';
import { CrmFieldService } from '@galaxy/crm/services/field-service';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ChangeVisibilityDialogComponent } from '../meeting-page/change-visibility-dialog/change-visibility-dialog.component';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { PartnerModelApiService } from '@vendasta/partner';
import { CRMApiService } from '@vendasta/crm';
import { UpcomingColumns, UpcomingConversationRow, UpcomingDataSourceService } from './upcoming-data-source.service';
import { OverrideVisibilityDialogComponent } from '../meeting-page/override-visibility-dialog/override-visibility-dialog.component';
import { RecordingInfoDialogComponent } from './meeting-bot-list-item/recording-info-dialog/recording-info-dialog.component';
import { RecordingsUpgradeComponent } from '../meeting-page/recording-upgrade-prompt/recordings-upgrade.component';
import { MeetingListService } from '../meeting-list.service';

@Component({
  selector: 'meeting-analysis-meeting-list',
  templateUrl: './meeting-list.component.html',
  styleUrls: ['./meeting-list.component.scss'],
  imports: [
    CommonModule,
    GalaxyEmptyStateModule,
    TranslateModule,
    KeyValuePipe,
    MatExpansionModule,
    DatePipe,
    MeetingListItemComponent,
    GalaxyFormFieldModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinnerModule,
    GalaxyAlertModule,
    MatIconModule,
    MatButtonToggleModule,
    MeetingBotListItemComponent,
    GalaxyTableModule,
    MatTableModule,
    GalaxyPageModule,
    GalaxyFilterChipsModule,
    ConversationPagePathPipe,
    RouterModule,
    MatMenuModule,
    MatButtonModule,
    GalaxyTooltipModule,
    RecordingsUpgradeComponent,
  ],
  providers: [
    {
      provide: ConversationDataSourceService,
      useFactory: (
        meetingAnalysisApiService: MeetingAnalysisApiService,
        translateService: TranslateService,
        snackbarService: SnackbarService,
        contactService: CRMApiService,
        crmFieldService: CrmFieldService,
        namespace$: Observable<string>,
        namespaceName$: Observable<string>,
        userId$: Observable<string>,
      ) => {
        return new ConversationDataSourceService(
          meetingAnalysisApiService,
          translateService,
          snackbarService,
          namespace$,
          namespaceName$,
          userId$,
          contactService,
          crmFieldService,
        );
      },
      deps: [
        MeetingAnalysisApiService,
        TranslateService,
        SnackbarService,
        CRMApiService,
        CrmFieldService,
        MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$,
        MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$,
        MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$,
      ],
    },
    {
      provide: UpcomingDataSourceService,
      deps: [
        MeetingBotApiService,
        TranslateService,
        SnackbarService,
        PartnerModelApiService,
        MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$,
        MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$,
      ],
    },
    { provide: GalaxyFilterChipInjectionToken, useExisting: ConversationDataSourceService },
  ],
})
export class MeetingListComponent implements OnInit {
  private readonly meetingAnalysisApiService = inject(MeetingAnalysisApiService);
  private readonly meetingBotApiService = inject(MeetingBotApiService);
  private readonly alertService = inject(SnackbarService);
  readonly namespaceName$ = inject(MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$);
  private readonly translateService = inject(TranslateService);
  private readonly upcomingDataSourceService = inject(UpcomingDataSourceService);
  private readonly meetingListService = inject(MeetingListService);

  loading = signal(true);
  searchTerm = signal('');
  searchTerm$ = toObservable(this.searchTerm).pipe(debounceTime(300));
  upcomingOrPast = signal<'upcoming' | 'past'>('past');
  listOrTableView = signal<'list' | 'table'>('list');

  conversations$$: BehaviorSubject<Conversation[]> = new BehaviorSubject<Conversation[]>([]);
  upcomingMeetings$$: BehaviorSubject<MeetingBot[]> = new BehaviorSubject<MeetingBot[]>([]);

  conversationsByDate$ = this.conversations$$.pipe(
    map((conversations) => {
      return groupByDate(conversations, (c) => c.meetingDate);
    }),
  );

  upcomingMeetingsByDate$ = this.upcomingMeetings$$.pipe(
    map((meetingBots) => {
      return groupByDate(meetingBots, (mb) => mb.eventStartTime);
    }),
  );

  /* This is because it's a pain to test as a different user.
   * If you're a superadmin you have access to can see other users meetings but there's nowhere in the UI to select that user.
   * This means for debugging purposes we can only see our own users' stuff.
   * This makes it easier to look at other peoples meetings to ensure everything is working without having to connect and make a bunch of fake meetings on prod
   * */
  debugUserId$ = this.route.queryParamMap.pipe(map((params) => params.get('debugUserId')));

  userId$ = combineLatest([this.defaultUserId$, this.debugUserId$]).pipe(
    map(([defaultUserId, debugUserId]) => debugUserId || defaultUserId),
  );

  dataSource!: GalaxyDataSource<ConversationRow, GalaxyFilterInterface, void>;
  pastRecordingsTableColumns = ConversationColumns;

  upcomingDataSource!: GalaxyDataSource<UpcomingConversationRow, GalaxyFilterInterface, void>;
  upcomingTableColumns = UpcomingColumns;

  recordingsUpgradeRequired = signal<boolean | undefined>(undefined);

  privacyLevel = PrivacyLevel;

  constructor(
    @Inject(MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
    @Inject(MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$) private readonly defaultUserId$: Observable<string>,
    @Inject(MEETING_ANALYSIS_ENABLED_INJECTION_TOKEN$) private readonly hasSalesCoach$: Observable<boolean>,
    @Inject(MEETING_ANALYSIS_CONTEXT_INJECTION_TOKEN$) private readonly context: Context,
    private readonly destroyRef: DestroyRef,
    private readonly route: ActivatedRoute,
    private readonly conversationDataSourceService: ConversationDataSourceService,
    protected readonly routeService: MeetingAnalysisRouterService,
    private dialog: MatDialog,
  ) {
    this.dataSource = new GalaxyDataSource<ConversationRow, GalaxyFilterInterface, void>(
      this.conversationDataSourceService,
    );
    this.upcomingDataSource = new GalaxyDataSource<UpcomingConversationRow, GalaxyFilterInterface, void>(
      this.upcomingDataSourceService,
    );

    effect(() => {
      combineLatest([this.hasSalesCoach$, this.conversations$$])
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(([hasSalesCoach, conversations]) => {
            return this.context === Context.PARTNER && !hasSalesCoach && conversations.length === 0;
          }),
        )
        .subscribe((recordingsUpgradeRequired) => {
          this.recordingsUpgradeRequired.set(recordingsUpgradeRequired);
        });
    });

    effect(() => {
      const upcomingOrPast = this.upcomingOrPast();
      this.loading.set(true);
      if (upcomingOrPast === 'past') {
        this.conversations$$.next([]);
        this.loadConversations();
      } else {
        this.upcomingMeetings$$.next([]);
        this.loadUpcomingMeetings();
      }
    });
  }

  onFilterChanged(filters: GalaxyFilterInterface[]): void {
    this.dataSource.setFilters(filters);
  }

  ngOnInit(): void {
    this.initViewType();

    this.searchTerm$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skip(1), // we skip the first one because it's empty string and the upcomingOrPast effect triggers this
      )
      .subscribe(() => {
        this.loading.set(true);
        if (this.upcomingOrPast() === 'past') {
          this.loadConversations();
        } else {
          this.loadUpcomingMeetings();
        }
      });
  }

  loadUpcomingMeetings(): void {
    combineLatest([this.namespace$, this.userId$, this.searchTerm$])
      .pipe(
        take(1),
        switchMap(([namespace, userId, searchTerm]) => {
          return this.meetingBotApiService.listMeetingBots({
            filters: {
              namespace: namespace,
              userId: userId,
              eventStartTimeAfter: new Date(),
            },
            searchTerm,
            pagingOptions: {
              pageSize: 50,
              cursor: '',
            },
          });
        }),
        catchError(() => {
          this.alertService.openErrorSnack('MEETING_LIST.ERROR_LOADING_MEETINGS');
          return of(null);
        }),
        map((response) => response?.meetingBots || []),
        map((meetingBots) => this.upcomingMeetings$$.next(meetingBots)),
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
        },
      });
  }

  loadConversations(): void {
    this.searchTerm$
      .pipe(
        take(1),
        switchMap((searchTerm) => {
          return this.meetingListService.listRecordings(searchTerm);
        }),
        map((conversations) => this.conversations$$.next(conversations)),
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
        },
      });
  }

  dateDesc = (a: KeyValue<string, object>, b: KeyValue<string, object>): number => {
    if (a.key < b.key) return 1;
    if (a.key > b.key) return -1;
    return 0;
  };

  dateAsc = (a: KeyValue<string, object>, b: KeyValue<string, object>): number => {
    if (a.key > b.key) return 1;
    if (a.key < b.key) return -1;
    return 0;
  };

  editVisibility(cr: ConversationRow): void {
    this.dialog
      .open(ChangeVisibilityDialogComponent, {
        width: '500px',
        data: {
          conversationId: cr.id,
          namespace: cr.namespace,
          privacyLevel: cr.privacyLevel,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          // Refresh the table to show the new privacy level
          this.dataSource.setSorting([]);
        }
      });
  }

  viewTypeToggleConst = 'recordings-view-type-selected';

  initViewType(): void {
    const savedViewType = localStorage.getItem(this.viewTypeToggleConst);
    if (savedViewType === 'list' || savedViewType === 'table') {
      this.listOrTableView.set(savedViewType);
    }
  }

  viewTypeToggled(): void {
    localStorage.setItem(this.viewTypeToggleConst, this.listOrTableView());
  }

  getPrivacyLevelInfo(privacyLevel: PrivacyLevel, namespaceName: string | null): { icon: string; tooltip: string } {
    if (privacyLevel === PrivacyLevel.PRIVACY_LEVEL_PRIVATE) {
      return {
        icon: 'lock',
        tooltip: this.translateService.instant('VISIBILITY.PRIVATE'),
      };
    }

    if (privacyLevel === PrivacyLevel.PRIVACY_LEVEL_UNLISTED) {
      return {
        icon: 'lock_open',
        tooltip: this.translateService.instant('VISIBILITY.UNLISTED_V2', { namespaceName: namespaceName || '' }),
      };
    }

    return {
      icon: 'group',
      tooltip: this.translateService.instant('VISIBILITY.OPEN', { namespaceName: namespaceName || '' }),
    };
  }

  openVisibilityInfoDialog(): void {
    this.dialog.open(RecordingInfoDialogComponent, {
      width: '500px',
    });
  }

  overrideMeetingBotPrivacyDialog(ucr: UpcomingConversationRow): void {
    this.dialog
      .open(OverrideVisibilityDialogComponent, {
        width: '500px',
        data: {
          meetingBotId: ucr.id,
          selectedPrivacyLevel: ucr.privacyLevel,
          overriddenPrivacy: ucr.overriddenPrivacy,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.upcomingDataSource.setSorting([]);
        }
      });
  }
}

function groupByDate<T>(things: T[], dateGetter: (t: any) => Date): Map<string, T[]> {
  const grouped = new Map<string, T[]>();
  things.forEach((thing) => {
    const date = format(dateGetter(thing), 'yyyy-MM-dd');
    if (!grouped.has(date)) {
      grouped.set(date, []);
    }
    grouped.get(date)!.push(thing);
  });
  return grouped;
}
